import { useTranslation } from '@frontend/i18n';
import { PanelContent, PanelPortal, PanelHeader } from '@frontend/panel-engine';
import { useBreakpoint } from '@frontend/responsiveness';
import { ModalControlModalProps, Tray, Heading, Button, useThemeValues } from '@frontend/design-system';
import { VerifyInsuranceForm } from './verify-insurance-form';

interface Props extends ModalControlModalProps {
  onReportLearnMoreClick: () => void;
  closeInsuranceSidePanel: () => void;
}

type VerifyInsuranceSidePanelProps = Omit<Props, 'closeInsuranceSidePanel'> & {
  // Set this true for a scenario where only the modal should be used instead of PanelEngine
  useModal?: boolean;
};

const VerifyInsurancePanelEngineDesktopView = ({ onReportLearnMoreClick, closeInsuranceSidePanel }: Props) => {
  const { t } = useTranslation('insuranceDetails');

  return (
    <PanelPortal id='weave-insurance-verification' width='medium' onClose={closeInsuranceSidePanel} enableAnimations>
      <PanelHeader>
        <PanelHeader.Title level={2}>{t('Verify Insurance')} </PanelHeader.Title>
        <PanelHeader.Close />
      </PanelHeader>
      <PanelContent>
        <VerifyInsuranceForm onClose={closeInsuranceSidePanel} onReportLearnMoreClick={onReportLearnMoreClick} />
      </PanelContent>
    </PanelPortal>
  );
};
const VerifyInsuranceSideTrayMobileView = ({
  onReportLearnMoreClick,
  closeInsuranceSidePanel,
  ...modalProps
}: Props) => {
  const { t } = useTranslation('insuranceDetails');
  const theme = useThemeValues();
  return (
    <Tray {...modalProps} width='medium' css={{ padding: '0' }} disableCloseOnEscape>
      <Tray.Header
        css={{ alignItems: 'flex-start', padding: theme.spacing(4) }}
        Buttons={
          <Button iconName='x' variant='secondary' size='large' label='close' onClick={closeInsuranceSidePanel} />
        }
      >
        <Heading level={2}>{t('Verify Insurance')}</Heading>
      </Tray.Header>
      <Tray.Body css={{ padding: theme.spacing(0, 4, 4, 4) }}>
        <VerifyInsuranceForm onClose={closeInsuranceSidePanel} onReportLearnMoreClick={onReportLearnMoreClick} />
      </Tray.Body>
    </Tray>
  );
};

export const VerifyInsuranceSidePanel = ({
  useModal = false,
  onReportLearnMoreClick,
  ...modalProps
}: VerifyInsuranceSidePanelProps) => {
  const size = useBreakpoint();
  const isDesktop = size === 'large' || size === 'xlarge';

  /**
   * When openModal is triggered from useModalControl then modalProps.show is set to true and sidePanel is displayed.
   * When onClose is triggered from useModalControl then modalProps.show is set to false and sidepanel is not displayed.
   */
  if (!modalProps.show) {
    return null;
  }
  if (isDesktop && !useModal) {
    return (
      <VerifyInsurancePanelEngineDesktopView
        {...modalProps}
        onReportLearnMoreClick={onReportLearnMoreClick}
        closeInsuranceSidePanel={modalProps.onClose}
      />
    );
  }
  return (
    <VerifyInsuranceSideTrayMobileView
      {...modalProps}
      onReportLearnMoreClick={onReportLearnMoreClick}
      closeInsuranceSidePanel={modalProps.onClose}
    />
  );
};
